import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTranslationApp } from "../../../../lib/i18next";
import {
  AUTOMATIONS_REVIEW_NAME,
  AUTOMATIONS_REVIEW_PLATFORMS,
  AUTOMATIONS_REVIEW_LANGUAGE,
  AUTOMATIONS_REVIEW_SCHEDULE,
  AUTOMATIONS_REVIEW_SCHEDULE_VALUE,
  AUTOMATIONS_REVIEW_SOURCE,
  AUTOMATIONS_REVIEW_VALIDITY,
  AUTOMATIONS_REVIEW_VALIDITY_ALWAYS_ON,
  AUTOMATIONS_REVIEW_VALIDITY_END_AT,
  AUTOMATIONS_REVIEW_VALIDITY_START_AT,
  AUTOMATIONS_REVIEW_VALIDITY_START_NOW,
  AUTOMATIONS_REVIEW_VALIDITY_VALUE,
  AUTOMATIONS_SOURCE_ALL_TITLE,
  AUTOMATIONS_STEP_REVIEW_HELP,
  AUTOMATIONS_STEP_REVIEW_TITLE,
} from "../../../../i18n/keysTranslations";
import { getSelectedPlatformIds } from "../../../../utils/sharePlatformAccounts";
import PlatformLogo from "../../../Common/PlatformLogo";
import AutomationStepSection from "../AutomationStepSection";
import { AUTOMATION_SOURCE_ALL } from "../automationModalConstants";
import { getFrequencyLabelKey } from "../../../../utils/publishingAutomationFrequency";
import {
  CONTENT_LANGUAGE_PRESET,
  getContentLanguageOptions,
} from "../../../../utils/catalogs/contentLanguage";
import {
  END_MODE_ALWAYS_ON,
  START_MODE_NOW,
  wallDateFromValue,
} from "../../../../utils/publishingAutomationValidity";
import { subtleBorderSx, subtleDividerSx } from "../automationModalStyles";

const formatTimeForDisplay = (timeValue) => {
  if (!timeValue) {
    return "";
  }

  const [hours, minutes] = timeValue.split(":").map(Number);
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);

  return date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const formatDateForDisplay = (dateValue) => {
  const dateLabel = wallDateFromValue(dateValue);
  const parsed = dateValue instanceof Date ? dateValue : null;
  if (parsed && !Number.isNaN(parsed.getTime())) {
    return parsed.toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  }
  return dateLabel;
};

const StepReview = ({ formState, getFolderLabel }) => {
  const { t } = useTranslationApp();

  const sourceLabel =
    formState.sourceType === AUTOMATION_SOURCE_ALL
      ? t(AUTOMATIONS_SOURCE_ALL_TITLE)
      : getFolderLabel({ folderID: formState.folderID }) || "—";

  const selectedPlatformIds = getSelectedPlatformIds(formState.platforms);
  const languageOption = getContentLanguageOptions({
    preset: CONTENT_LANGUAGE_PRESET.ALL,
  }).find((option) => option.value === formState.language);
  const languageLabel = languageOption
    ? t(languageOption.label)
    : formState.language || "—";

  const platformLogos = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: { xs: "flex-start", sm: "flex-end" },
        gap: 0.5,
        flexWrap: "wrap",
      }}
    >
      {selectedPlatformIds.length > 0
        ? selectedPlatformIds.map((platform) => (
            <PlatformLogo
              key={platform}
              platform={platform}
              width={20}
              height={20}
              alt={platform}
            />
          ))
        : "—"}
    </Box>
  );

  const rows = [
    {
      label: AUTOMATIONS_REVIEW_NAME,
      value: formState.name || "—",
    },
    { label: AUTOMATIONS_REVIEW_SOURCE, value: sourceLabel },
    {
      label: AUTOMATIONS_REVIEW_LANGUAGE,
      value: languageLabel,
    },
    {
      label: AUTOMATIONS_REVIEW_PLATFORMS,
      value: platformLogos,
      isCustomValue: true,
    },
    {
      label: AUTOMATIONS_REVIEW_SCHEDULE,
      value: t(AUTOMATIONS_REVIEW_SCHEDULE_VALUE, {
        frequency: t(getFrequencyLabelKey(formState.frequencyKey)),
        startTime: formatTimeForDisplay(formState.startTime),
        endTime: formatTimeForDisplay(formState.endTime),
      }),
    },
    {
      label: AUTOMATIONS_REVIEW_VALIDITY,
      value: t(AUTOMATIONS_REVIEW_VALIDITY_VALUE, {
        start:
          formState.startMode === START_MODE_NOW
            ? t(AUTOMATIONS_REVIEW_VALIDITY_START_NOW)
            : t(AUTOMATIONS_REVIEW_VALIDITY_START_AT, {
                datetime: formatDateForDisplay(formState.validityStartDate),
              }),
        end:
          formState.endMode === END_MODE_ALWAYS_ON
            ? t(AUTOMATIONS_REVIEW_VALIDITY_ALWAYS_ON)
            : t(AUTOMATIONS_REVIEW_VALIDITY_END_AT, {
                datetime: formatDateForDisplay(formState.validityEndDate),
              }),
      }),
    },
  ];

  return (
    <AutomationStepSection
      title={t(AUTOMATIONS_STEP_REVIEW_TITLE)}
      description={t(AUTOMATIONS_STEP_REVIEW_HELP)}
    >
      <Box
        sx={{
          ...subtleBorderSx,
          px: { xs: 1.5, sm: 2 },
          minWidth: 0,
        }}
      >
        {rows.map((row, index) => (
          <Box
            key={row.label}
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              gap: { xs: 0.5, sm: 2 },
              py: 1.5,
              borderBottom: index < rows.length - 1 ? `1px solid` : 0,
              minWidth: 0,
              ...subtleDividerSx,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ minWidth: 0, flexShrink: 0 }}
            >
              {t(row.label)}
            </Typography>
            {row.isCustomValue ? (
              <Box
                sx={{
                  maxWidth: { xs: "100%", sm: "70%" },
                  width: { xs: "100%", sm: "auto" },
                  minWidth: 0,
                  "& > *": {
                    justifyContent: { xs: "flex-start", sm: "flex-end" },
                  },
                }}
              >
                {row.value}
              </Box>
            ) : (
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{
                  textAlign: { xs: "left", sm: "right" },
                  maxWidth: { xs: "100%", sm: "70%" },
                  width: { xs: "100%", sm: "auto" },
                  minWidth: 0,
                  overflowWrap: "anywhere",
                }}
              >
                {row.value}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    </AutomationStepSection>
  );
};

export default StepReview;
